import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, finalize, map, switchMap, tap} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {ModalService} from '../../shared/services/modal.service';
import {UserService} from '../shared/user.service';
import {UserInfo} from '../shared/user-info.model';
import {LoaderService} from '../../shared/services/loader.service';
import {RolesLabelEnum} from '../shared/role.model';
import {TranslateService} from '@ngx-translate/core';
import {RolesLabelService} from '../shared/rolesLabel.service';
import {UserSearchCriteria} from "../../shared/user-search-criteria";

@Component({
  selector: 'app-overdue-manager-list',
  templateUrl: './overdue-manager-list.component.html',
  styleUrls: ['./overdue-manager-list.component.scss']
})
export class OverdueManagerListComponent implements OnInit {

  public managers: UserInfo[] = [];
  public chepManagers: UserInfo[] = [];
  public selectedChepManagers: UserInfo[] = [];
  public filters = new UserSearchCriteria();

  @ViewChild('userTable')
  userTable;

  loadPage = (page: number, pageSize: number) => this.rolesLabelService.searchUsersByRole(RolesLabelEnum.OVERDUE_MANAGER, page, pageSize, this.filters);

  constructor(
    private rolesLabelService: RolesLabelService,
    private translateService: TranslateService,
    private userService: UserService,
    private modalService: ModalService,
    private loader: LoaderService,
    private toastr: ToastrService,
  ) {
    this.loader.startLoad(false);

    this.rolesLabelService.getAllUsersByRole(RolesLabelEnum.OVERDUE_MANAGER).pipe(
      tap(managers => this.managers = managers),
      catchError((error: HttpErrorResponse) => {
        this.toastr.error(error.error.message
          || this.translateService.instant('user-dashboard.users.blue-tracking-managers.list.error'));
        return [];
      }),
      finalize(() => this.loader.stopLoad())
    ).subscribe();
  }

  public ngOnInit() {
  }

  public addOverdueManagerRoleToExistingUser(content: TemplateRef<any>): void {
    let chepManagersObservable;

    if (this.chepManagers.length === 0) {
      chepManagersObservable = this.fetchEligibleChepManagers(this.managers);
    } else {
      chepManagersObservable = of(this.chepManagers);
    }

    const modalObservable = this.modalService.open({
      title: this.translateService.instant('user-dashboard.users.blue-tracking-managers.add-role-modal.title'),
      content,
      closeButton: {
        text: this.translateService.instant('buttons.cancel'),
      },
      validateButton: {
        text: this.translateService.instant('buttons.validate'),
        disabled: () => this.selectedChepManagers.length === 0
      },
    }).pipe(
      switchMap(() => this.rolesLabelService.addRoleToUsers(RolesLabelEnum.OVERDUE_MANAGER, this.selectedChepManagers)),
    )

    chepManagersObservable
      .pipe(
        tap((chepManagers: UserInfo[]) => this.chepManagers = chepManagers),
        switchMap(() => modalObservable),
        finalize(() => this.selectedChepManagers = [])
      ).subscribe(
      () => {
        this.userTable.getPage(this.userTable.page, this.userTable.pageSize);
        this.toastr.success(this.translateService.instant('user-dashboard.users.blue-tracking-managers.add-role-modal.success'))
      },
      (error) => this.toastr.error(error.error.message
        || this.translateService.instant('user-dashboard.users.blue-tracking-managers.add-role-modal.error'))
    );
  }

  public openRoleRemovalConfirmationPopup(content: TemplateRef<any>, userId: string) {
    this.modalService.open({
      title: this.translateService.instant('user-dashboard.users.blue-tracking-managers.remove-role-modal.title'),
      content,
      closeButton: {
        text: this.translateService.instant('buttons.cancel'),
      },
      validateButton: {
        text: this.translateService.instant('buttons.validate'),
      },
    }).pipe(
      switchMap(() => this.rolesLabelService.removeRoleOfUser(RolesLabelEnum.OVERDUE_MANAGER, userId)),
    ).subscribe(
      () => {
        this.userTable.getPage(this.userTable.page, this.userTable.pageSize);
        this.toastr.success(this.translateService.instant('user-dashboard.users.blue-tracking-managers.remove-role-modal.success'))
      },
      (error) => this.toastr.error(error.error.message
        || this.translateService.instant('user-dashboard.users.blue-tracking-managers.remove-role-modal.error'))
    );
  }

  private fetchEligibleChepManagers(overdueManagers: UserInfo[]): Observable<UserInfo[]> {
    this.loader.startLoad();

    const overdueManagerIds = overdueManagers.map(podReviewer => podReviewer.id);

    return this.userService.getChepManagers()
      .pipe(
        map(chepManagers => chepManagers.filter(chepManager => !overdueManagerIds.includes(chepManager.id))),
        finalize(() => this.loader.stopLoad()),
        catchError((error: HttpErrorResponse) => {
          console.log(error);
          this.toastr.error(error.error.message
            || this.translateService.instant('user-dashboard.users.blue-tracking-managers.list.error-chep-managers'));
          return [];
        })
      );
  }
}
